import React, { useState } from 'react';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import { IIRChartDataPoint, IIRChartData } from './index';

import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Bar } from 'react-chartjs-2';

export interface IBarChartProps extends IBaseElementProps {
	chartDataProps?: IIRChartData;
	chartIndex?: number;
}

const BarChart: React.FC<IBarChartProps> = ({
	chartDataProps,
	chartIndex,
}) => {
	// Register required Chart.js elements and plugins
	ChartJS.register(
		CategoryScale,
		LinearScale,
		BarElement,
		ChartDataLabels,
		annotationPlugin,
	);

	// Variables for linear gradient creation function
	let width;
	let height;
	let gradient;

	// Function to generate gradient for chart
	function getGradient(ctx, chartArea) {
		const chartWidth = chartArea.right - chartArea.left;
		const chartHeight = chartArea.bottom - chartArea.top;
		if (!gradient || width !== chartWidth || height !== chartHeight) {
			width = chartWidth;
			height = chartHeight;
			gradient = ctx.createLinearGradient(
				0,
				chartArea.bottom,
				0,
				chartArea.top,
			);
			gradient.addColorStop(0, '#007BC7');
			gradient.addColorStop(1, '#2308C9');
		}
		return gradient;
	}

	// Function to calculate y value for annotations
	function yValue(ctx, label) {
		const chart = ctx.chart;
		const dataset = chart.data.datasets[0];
		return dataset.data[chart.data.labels.indexOf(label)] / 2;
	}

	// Annotation options
	const annotationOptions = {
		type: 'label',
		font: {
			family: "'Plus Jakarta Sans', sans-serif",
			size: 12,
			weight: '600',
			lineHeight: '1.5',
		},
		color: '#2e2e2e',
		backgroundColor: '#ffffff',
		borderColor: '#ffffff',
		borderRadius: 32,
		borderWidth: 1,
		backgroundShadowColor: 'rgba(57, 79, 121, 0.12)',
		shadowBlur: 3,
		shadowOffsetX: 0,
		shadowOffsetY: 3,
		padding: {
			top: 4,
			right: 8,
			bottom: 4,
			left: 8,
		},
		content: ['21.0%'],
		position: {
			x: 'center',
		},
		xValue: '',
		yValue: (ctx) => yValue(ctx, ''),
	};

	// Chart default options
	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		layout: {
			padding: {
				top: 20,
			},
		},
		plugins: {
			legend: {
				display: false,
			},
			datalabels: {
				labels: {
					value: {
						font: {
							family: "'Plus Jakarta Sans', sans-serif",
							size: 14,
							weight: 600,
						},
						color: '#2E2E2E',
						anchor: 'end' as const,
						clamp: true,
						align: 'top' as const,
					},
				},
			},
			annotation: {
				annotations: {},
			},
		},
		barPercentage: 1,
		maxBarThickness: 40,
		scales: {
			x: {
				grid: {
					display: false,
					borderColor: 'transparent',
				},
			},
			y: {
				display: false,
			},
		},
	};

	// Function to create annotations
	function createAnnotations(xAxisLabels, dataLabels, dataLabelsPositions) {
		xAxisLabels.forEach((item, index) => {
			let annotationObjectUpdate = Object.create(annotationOptions);
			annotationObjectUpdate.content = [dataLabels[index]];
			annotationObjectUpdate.xValue = item;
			if (dataLabelsPositions[index] != undefined) {
				annotationObjectUpdate.yValue = dataLabelsPositions[index];
			} else {
				annotationObjectUpdate.yValue = (ctx) => yValue(ctx, item);
			}
			chartOptions.plugins.annotation.annotations[`annotation${index}`] =
				annotationObjectUpdate;
		});
	}

	// Function to create data source for chart
	function createChartDataSource(
		dataSetName,
		dataSetXAxisLabels,
		dataSetValues,
	) {
		const dataSource = {
			labels: dataSetXAxisLabels,
			datasets: [
				{
					label: dataSetName,
					data: dataSetValues,

					backgroundColor: function (context) {
						const chart = context.chart;
						const { ctx, chartArea } = chart;

						if (!chartArea) {
							return;
						}

						const returnColors = dataSetValues.map(
							(item, index) => {
								return index === dataSetValues.length - 1
									? getGradient(ctx, chartArea)
									: '#D9D9D9';
							},
						);

						return returnColors as any;
					},
					borderRadius: 8,
					borderSkipped: false,
				},
			],
		};

		return dataSource;
	}

	// Variables to store chart data
	let chartDatasetXAxisLabels = [];
	let chartDatasetValues = [];
	let chartDatasetDataLabels = [];
	let chartDatasetDataLabelsPositions = [];
	let chartSecondaryDatasetXAxisLabels = [];
	let chartSecondaryDatasetValues = [];
	let chartSecondaryDatasetDataLabels = [];
	let chartSecondaryDatasetDataLabelsPositions = [];

	// Populating chart data variables
	chartDataProps.chartFirstDataset.forEach(
		(item: IIRChartDataPoint) => {
			chartDatasetXAxisLabels.push(item.xAxisLabel);
			chartDatasetValues.push(item.yAxisValue);
			chartDatasetDataLabels.push(item.dataLabel);
			chartDatasetDataLabelsPositions.push(item.dataLabelPosition);
		},
	);

	// If secondary dataset is present, populate secondary chart data variables
	if (
		chartDataProps.chartSecondaryDataset &&
		chartDataProps.chartSecondaryDataset.length > 0
	) {
		chartDataProps.chartSecondaryDataset.forEach(
			(item: IIRChartDataPoint) => {
				chartSecondaryDatasetXAxisLabels.push(item.xAxisLabel);
				chartSecondaryDatasetValues.push(item.yAxisValue);
				chartSecondaryDatasetDataLabels.push(item.dataLabel);
				chartSecondaryDatasetDataLabelsPositions.push(
					item.dataLabelPosition,
				);
			},
		);
	}

	const dataSourceOne = createChartDataSource(
		'Dataset 1',
		chartDatasetXAxisLabels,
		chartDatasetValues,
	);

	const dataSourceTwo = createChartDataSource(
		'Dataset 2',
		chartSecondaryDatasetXAxisLabels,
		chartSecondaryDatasetValues,
	);

	const [chartData, setChartData] = useState(dataSourceOne);
	const [currentDatasetXAxisLabels, setCurrentDatasetXAxisLabels] = useState(
		chartDatasetXAxisLabels,
	);
	const [currentDatasetDataLabels, setCurrentDatasetDataLabels] = useState(
		chartDatasetDataLabels,
	);
	const [
		currentDatasetDataLabelsPositions,
		setCurrentDatasetDataLabelsPositions,
	] = useState(chartDatasetDataLabelsPositions);
	const [isRadio, setIsRadio] = useState(1);

	if (chartDataProps.chartShowDataLabels) {
		createAnnotations(
			currentDatasetXAxisLabels,
			currentDatasetDataLabels,
			currentDatasetDataLabelsPositions,
		);
	}

	const handleRadioChange = (e) => {
		if (e.target.value === 'data-1-' + chartIndex) {
			setChartData(dataSourceOne);
			setCurrentDatasetXAxisLabels(chartDatasetXAxisLabels);
			setCurrentDatasetDataLabels(chartDatasetDataLabels);
			setCurrentDatasetDataLabelsPositions(
				chartDatasetDataLabelsPositions,
			);
			setIsRadio(1);
		} else {
			setChartData(dataSourceTwo);
			setCurrentDatasetXAxisLabels(chartSecondaryDatasetXAxisLabels);
			setCurrentDatasetDataLabels(chartSecondaryDatasetDataLabels);
			setCurrentDatasetDataLabelsPositions(
				chartSecondaryDatasetDataLabelsPositions,
			);
			setIsRadio(2);
		}
	};

	return (
		<div className="calian-ir-charts__bar-chart--container">
			{chartDataProps.chartHeading && (
				<p className="calian-ir-charts__bar-chart--heading">
					{chartDataProps.chartHeading}
				</p>
			)}
			{chartDataProps.chartSubHeading && (
				<p className="calian-ir-charts__bar-chart--sub-heading">
					{chartDataProps.chartSubHeading}
				</p>
			)}
			<div className="calian-ir-charts__bar-chart">
				<Bar options={chartOptions} data={chartData} />
			</div>
			{chartDataProps.chartSecondaryDataset &&
				chartDataProps.chartSecondaryDataset.length > 0 && (
					<div className="calian-ir-charts__dataset-select">
						<div className="calian-ir-charts__dataset-select--left">
							<label
								className="form-check-label"
								htmlFor={`primary-dataset-radio-input-${chartIndex}`}
							>
								{chartDataProps.chartFirstDatasetLabel &&
									chartDataProps.chartFirstDatasetLabel}
								<input
									className="form-check-input"
									type="radio"
									id={`primary-dataset-radio-input-${chartIndex}`}
									value={`data-1-${chartIndex}`}
									onChange={handleRadioChange}
									checked={isRadio === 1}
								/>
								<span className="custom-radio"></span>
							</label>
						</div>
						<div className="calian-ir-charts__dataset-select--right">
							<label
								className="form-check-label"
								htmlFor={`secondary-dataset-radio-input-${chartIndex}`}
							>
								{chartDataProps.chartSecondaryDatasetLabel &&
									chartDataProps.chartSecondaryDatasetLabel}
								<input
									className="form-check-input"
									type="radio"
									id={`secondary-dataset-radio-input-${chartIndex}`}
									value={`data-2-${chartIndex}`}
									onChange={handleRadioChange}
									checked={isRadio === 2}
								/>
								<span className="custom-radio"></span>
							</label>
						</div>
					</div>
				)}
		</div>
	);
};

export default BarChart;
